import {
  ChoicePicker,
  ChoicePickerPanel,
  ChoicePickerTrigger,
  type ChoiceFilter,
  type ChoiceItem,
} from "@/components/molecules/choice-picker";
import {
  IntegrationLogo,
  IntegrationName,
} from "@/components/molecules/integration-logo";
import { Button } from "@/components/ui/button";
import { useIntegrationName } from "@/features/integrations/hooks/use-integration-name";
import { languageFlag, languageName } from "@/features/utils/display-names";
import { useTranslation } from "@/features/utils/i18n";
import {
  hasTemperamentSample,
  useSamplePlayer,
  type SampleOptions,
} from "@/features/voices/hooks/use-sample-player";
import type { Voice, VoiceProfile, VoiceStyle } from "@/features/voices/types";
import {
  CircleNotchIcon,
  PauseIcon,
  PlayIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import _ from "lodash";
import { useMemo, useState } from "react";

type Gender = "female" | "male" | "other";
type GenderFilter = "all" | "female" | "male";

const genderOf = (voice: Voice): Gender => {
  const gender = (voice.gender || "").toLowerCase();
  if (gender === "female" || gender === "f") return "female";
  if (gender === "male" || gender === "m") return "male";
  return "other";
};

/** "fr-FR" and "fr" are the same choice here: a voice is filed under its language. */
const baseLanguage = (code?: string | null) =>
  (code || "").toLowerCase().split(/[-_]/)[0];

interface VoicePickerProps {
  id?: string;
  /** The voices that may be chosen. */
  voices: Voice[];
  /** The agent's current voice id. */
  value?: string;
  /**
   * The agent's current voice when it is no longer among `voices`: shown so the
   * field is not blank, and gone as soon as another one is picked.
   */
  unavailableVoice?: Voice;
  /** Which language the list opens on — the company's. */
  defaultLanguage?: string | null;
  /**
   * The agent's temperament: an ElevenLabs voice is previewed in it (the server generates
   * that preview on first request), so the voice is judged as it will really sound.
   */
  temperament?: string;
  onChange: (voiceId: string) => void;
}

/**
 * The voice field of an agent: a button showing the current voice, opening a
 * picker (`ChoicePicker`) where every voice can be heard before it is chosen.
 */
export function VoicePicker({
  id,
  voices,
  value,
  unavailableVoice,
  defaultLanguage,
  temperament,
  onChange,
}: VoicePickerProps) {
  const { t, i18n } = useTranslation("voicePicker");
  const [open, setOpen] = useState(false);

  const current =
    voices.find((voice) => voice.id === value) ||
    (unavailableVoice?.id === value ? unavailableVoice : undefined);

  const player = useSamplePlayer();

  return (
    <>
      <div className="flex gap-2">
        <ChoicePickerTrigger
          id={id}
          onClick={() => setOpen(true)}
          placeholder={t("voicePicker.placeholder")}
          label={current?.name}
          meta={
            current && (
              <>
                {[
                  t(`voicePicker.gender.${genderOf(current)}`),
                  current.language &&
                    `${languageFlag(current.language)} ${languageName(current.language, i18n.language)}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                {current === unavailableVoice && (
                  <span className="text-destructive">
                    {" "}
                    — {t("voicePicker.unavailable")}
                  </span>
                )}
              </>
            )
          }
        />
        {current && canPlay(current) && (
          <SamplePlayButton
            voice={current}
            player={player}
            options={{
              temperament,
              language: previewLanguage(current, defaultLanguage),
            }}
          />
        )}
      </div>

      <ChoicePicker
        open={open}
        onOpenChange={setOpen}
        title={t("voicePicker.title")}
        description={t("voicePicker.description")}
      >
        <VoicePickerPanel
          voices={voices}
          value={value}
          unavailableVoice={unavailableVoice}
          defaultLanguage={defaultLanguage}
          temperament={temperament}
          onCancel={() => setOpen(false)}
          onConfirm={(voiceId) => {
            onChange(voiceId);
            setOpen(false);
          }}
        />
      </ChoicePicker>
    </>
  );
}

/** Every style, in the order the chips show them. */
const STYLES: VoiceStyle[] = [
  "calm",
  "warm",
  "casual",
  "professional",
  "energetic",
  "expressive",
];

const ALL = "all";

/**
 * The languages a voice can be chosen for: the ones it was checked in, or its
 * own when we know nothing more about it.
 */
const spokenLanguages = (voice: Voice) =>
  voice.profile?.languages.length
    ? voice.profile.languages.map(baseLanguage)
    : [baseLanguage(voice.language)].filter(Boolean);

/**
 * The language a voice is previewed in when no language is picked: the company's, if the
 * voice speaks it, else the voice's own.
 */
const previewLanguage = (voice: Voice, defaultLanguage?: string | null) => {
  const base = baseLanguage(defaultLanguage);
  return base && spokenLanguages(voice).includes(base)
    ? base
    : baseLanguage(voice.language);
};

const describe = (profile: VoiceProfile | undefined, uiLanguage?: string) =>
  profile &&
  (baseLanguage(uiLanguage) === "fr"
    ? profile.description.fr
    : profile.description.en);

const canPlay = (voice: Voice) => !!voice.sample || hasTemperamentSample(voice);

/**
 * Plays or pauses a voice's sample: beside the field (`field`), or at the end of a
 * row of the list (`row`), where it also says when a voice has nothing to hear.
 */
function SamplePlayButton({
  voice,
  player,
  options,
  variant = "field",
  tabIndex,
}: {
  voice: Voice;
  player: ReturnType<typeof useSamplePlayer>;
  options: SampleOptions;
  variant?: "field" | "row";
  tabIndex?: number;
}) {
  const { t } = useTranslation("voicePicker");
  const playable = canPlay(voice);
  const isPlaying = player.playingId === voice.id;
  const isLoading = player.loadingId === voice.id;
  const row = variant === "row";

  return (
    <Button
      type="button"
      variant={row ? (isPlaying ? "default" : "ghost") : "outline"}
      size="icon"
      className={row ? "h-8 w-8 shrink-0 rounded-full" : undefined}
      tabIndex={tabIndex}
      disabled={!playable}
      onClick={() => player.toggle(voice, options)}
      aria-pressed={row ? isPlaying : undefined}
      aria-label={t(
        !playable
          ? "voicePicker.noSample"
          : isPlaying
            ? "voicePicker.pauseNamed"
            : "voicePicker.playNamed",
        { name: voice.name },
      )}
      title={playable ? undefined : t("voicePicker.noSample")}
    >
      {isLoading ? (
        <CircleNotchIcon className="h-4 w-4 animate-spin" />
      ) : isPlaying ? (
        <PauseIcon weight="fill" className="h-4 w-4" />
      ) : (
        <PlayIcon weight="fill" className="h-4 w-4" />
      )}
    </Button>
  );
}

/**
 * The picker's filters — their state, the options each one offers given the
 * voices, and the voices they leave, sorted.
 */
function useVoiceFilters(
  voices: Voice[],
  value: string | undefined,
  defaultLanguage: string | null | undefined,
) {
  const { i18n } = useTranslation("voicePicker");
  const uiLanguage = i18n.language;
  const integrationName = useIntegrationName();

  const [search, setSearch] = useState("");
  const [gender, setGender] = useState<GenderFilter>("all");
  const [styles, setStyles] = useState<string[]>([]);
  const [provider, setProvider] = useState(ALL);

  const languageOptions = useMemo(
    () =>
      _.sortBy(_.uniq(voices.flatMap(spokenLanguages)), (code) =>
        languageName(code, uiLanguage),
      ),
    [voices, uiLanguage],
  );
  const providerOptions = useMemo(
    () =>
      _.sortBy(
        _.uniq(voices.map((voice) => voice.provider_name).filter(Boolean)),
        (name) => integrationName(name),
      ),
    [voices, uiLanguage],
  );

  // Opens on the company's language — unless the agent's own voice does not
  // speak it, in which case on every language, so the checked row is on screen.
  const [language, setLanguage] = useState(() => {
    const base = baseLanguage(defaultLanguage);
    const selected = voices.find((voice) => voice.id === value);
    if (!languageOptions.includes(base)) return ALL;
    if (selected && !spokenLanguages(selected).includes(base)) return ALL;
    return base;
  });

  const inLanguage = useMemo(
    () =>
      voices.filter(
        (voice) =>
          language === ALL || spokenLanguages(voice).includes(language),
      ),
    [voices, language],
  );
  // Only the styles some voice of this language has: a chip that can only ever
  // empty the list is not a choice.
  const styleOptions = useMemo(
    () =>
      STYLES.filter((style) =>
        inLanguage.some((voice) => voice.profile?.styles.includes(style)),
      ),
    [inLanguage],
  );

  const filtered = useMemo(() => {
    // A style picked for another language has no chip here any more: it must not
    // keep filtering out of sight (and is back on if that language is).
    const activeStyles = styles.filter((style) =>
      styleOptions.includes(style as VoiceStyle),
    );
    const query = search.trim().toLocaleLowerCase();
    return _.sortBy(
      inLanguage.filter(
        (voice) =>
          (gender === "all" || genderOf(voice) === gender) &&
          (provider === ALL || voice.provider_name === provider) &&
          activeStyles.every((style) =>
            voice.profile?.styles.includes(style as VoiceStyle),
          ) &&
          (!query ||
            voice.name.toLocaleLowerCase().includes(query) ||
            (describe(voice.profile, uiLanguage) || "")
              .toLocaleLowerCase()
              .includes(query)),
      ),
      // A voice whose own language this is before one that also speaks it; with
      // every language listed, the company's first.
      (voice) => {
        const own = baseLanguage(voice.language);
        const first =
          language === ALL ? baseLanguage(defaultLanguage) : language;
        return own === first ? "" : languageName(own, uiLanguage);
      },
      (voice) => voice.name,
    );
  }, [
    inLanguage,
    styleOptions,
    styles,
    search,
    gender,
    provider,
    language,
    defaultLanguage,
    uiLanguage,
  ]);

  const reset = () => {
    setSearch("");
    setGender("all");
    setStyles([]);
    setProvider(ALL);
    setLanguage(ALL);
  };

  return {
    search,
    setSearch,
    gender,
    setGender,
    styles,
    setStyles,
    provider,
    setProvider,
    language,
    setLanguage,
    languageOptions,
    providerOptions,
    styleOptions,
    filtered,
    reset,
  };
}

/** The picker's body: the voice-specific filters, and the rows they leave. */
function VoicePickerPanel({
  voices,
  value,
  unavailableVoice,
  defaultLanguage,
  temperament,
  onCancel,
  onConfirm,
}: Omit<VoicePickerProps, "onChange" | "id"> & {
  onCancel: () => void;
  onConfirm: (voiceId: string) => void;
}) {
  const { t, i18n } = useTranslation("voicePicker");
  const integrationName = useIntegrationName();
  const player = useSamplePlayer();
  const {
    search,
    setSearch,
    gender,
    setGender,
    styles,
    setStyles,
    provider,
    setProvider,
    language,
    setLanguage,
    languageOptions,
    providerOptions,
    styleOptions,
    filtered,
    reset,
  } = useVoiceFilters(voices, value, defaultLanguage);

  const filters: ChoiceFilter[] = [
    {
      id: "language",
      label: t("voicePicker.language"),
      kind: "select",
      value: language,
      onChange: setLanguage,
      options: [
        { value: ALL, label: t("voicePicker.allLanguages") },
        ...languageOptions.map((code) => ({
          value: code,
          label: `${languageFlag(code)} ${languageName(code, i18n.language)}`,
        })),
      ],
    },
    {
      id: "gender",
      label: t("voicePicker.genderFilter"),
      kind: "segmented",
      value: gender,
      onChange: (next) => setGender(next as GenderFilter),
      options: (["all", "female", "male"] as const).map((option) => ({
        value: option,
        label: t(`voicePicker.genderOption.${option}`),
      })),
    },
    // One provider is not a choice — nor worth naming on every row: today the
    // agent form only offers ElevenLabs voices.
    ...(providerOptions.length > 1
      ? [
          {
            id: "provider",
            label: t("voicePicker.provider"),
            kind: "select" as const,
            value: provider,
            onChange: setProvider,
            options: [
              { value: ALL, label: t("voicePicker.allProviders") },
              ...providerOptions.map((name) => ({
                value: name,
                label: (
                  <span className="flex items-center gap-2">
                    <IntegrationLogo id={name} size="xs" />
                    {integrationName(name)}
                  </span>
                ),
              })),
            ],
          },
        ]
      : []),
    {
      id: "style",
      label: t("voicePicker.styleFilter"),
      kind: "chips",
      value: styles,
      onChange: setStyles,
      options: styleOptions.map((style) => ({
        value: style,
        label: t(`voicePicker.style.${style}`),
      })),
    },
  ];

  const traits = (voice: Voice) =>
    [
      t(`voicePicker.gender.${genderOf(voice)}`),
      voice.profile && t(`voicePicker.age.${voice.profile.age}`),
      voice.profile &&
        voice.profile.accent !== "standard" &&
        t(`voicePicker.accent.${voice.profile.accent}`),
    ]
      .filter(Boolean)
      .join(" · ");

  const items: ChoiceItem[] = filtered.map((voice) => {
    const others = spokenLanguages(voice).length - 1;
    return {
      id: voice.id,
      title: voice.name,
      meta: traits(voice),
      description: describe(voice.profile, i18n.language),
      tags: voice.profile?.styles.map((style) =>
        t(`voicePicker.style.${style}`),
      ),
      aside: voice.language && (
        <span
          className="flex flex-col items-end gap-0.5"
          title={
            others > 0
              ? spokenLanguages(voice)
                  .map((code) => languageName(code, i18n.language))
                  .join(", ")
              : undefined
          }
        >
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="text-base leading-none">
              {languageFlag(voice.language)}
            </span>
            {languageName(voice.language, i18n.language)}
          </span>
          {others > 0 && (
            <span>{t("voicePicker.otherLanguages", { count: others })}</span>
          )}
          {providerOptions.length > 1 && (
            <IntegrationName id={voice.provider_name} className="mt-0.5" />
          )}
        </span>
      ),
      action: ({ tabIndex }) => (
        <SamplePlayButton
          voice={voice}
          player={player}
          variant="row"
          tabIndex={tabIndex}
          options={{
            temperament,
            language:
              language === ALL
                ? previewLanguage(voice, defaultLanguage)
                : language,
          }}
        />
      ),
    };
  });

  return (
    <ChoicePickerPanel
      items={items}
      value={value}
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder={t("voicePicker.search")}
      filters={filters}
      listLabel={t("voicePicker.title")}
      emptyText={t("voicePicker.empty")}
      onResetFilters={reset}
      notice={(pending) =>
        !!unavailableVoice &&
        pending === unavailableVoice.id && (
          <div className="text-muted-foreground mx-6 mb-2 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs">
            <WarningIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <span>
              {t("voicePicker.unavailableNotice", {
                name: unavailableVoice.name,
              })}
            </span>
          </div>
        )
      }
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
